import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ArtworkCard from '../components/ArtworkCard';

const Home = () => {
  const [featuredArtworks, setFeaturedArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchFeaturedArtworks();
  }, []);

  const fetchFeaturedArtworks = async () => {
    try {
      // First try to get featured artworks
      let response = await axios.get(`${API_URL}/api/artworks?featured=true`);
      
      // If no featured artworks, get the latest 6 artworks instead
      if (response.data.length === 0) {
        console.log('No featured artworks found, showing latest artworks instead');
        response = await axios.get(`${API_URL}/api/artworks`);
      }
      
      setFeaturedArtworks(response.data.slice(0, 6));
    } catch (error) {
      console.error('Error fetching artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-orange-50 via-amber-50 to-red-50">
        {/* African Pattern Background */}
        <div className="absolute inset-0 african-pattern opacity-30"></div>
        
        {/* Animated Circles */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse-slow"></div>
        <div className="absolute bottom-20 left-20 w-72 h-72 bg-red-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-40 left-1/2 w-72 h-72 bg-amber-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse-slow" style={{ animationDelay: '2s' }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="animate-fade-in-up">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg mb-8">
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-semibold text-orange-900">Africa's Premier Art Marketplace</span>
            </div>

            {/* Main Heading */}
            <h1 className="artistic-title mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Discover African
              <br />
              Artistic Excellence
            </h1>

            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-gray-700 mb-10 max-w-3xl mx-auto font-light animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              Connect with talented African artists and own unique pieces that tell powerful stories through vibrant colors and cultural heritage.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
              <Link to="/marketplace" className="btn-primary text-lg">
                <span className="flex items-center space-x-2">
                  <span>Explore Marketplace</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Link>
              <Link to="/artists" className="btn-outline text-lg">
                Meet the Artists
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mt-16 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
              <div className="text-center">
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">500+</div>
                <div className="text-sm text-gray-600 mt-1">Artworks</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">100+</div>
                <div className="text-sm text-gray-600 mt-1">Artists</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">1000+</div>
                <div className="text-sm text-gray-600 mt-1">Happy Buyers</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Featured Artworks Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4 african-accent inline-block">
            {featuredArtworks.length > 0 && featuredArtworks.some(a => a.featured) 
              ? 'Featured Artworks' 
              : 'Latest Artworks'}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {featuredArtworks.length > 0 && featuredArtworks.some(a => a.featured)
              ? 'Handpicked masterpieces from our most talented artists'
              : 'Discover amazing artworks from talented African artists'}
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 aspect-square rounded-2xl mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : featuredArtworks.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {featuredArtworks.map((artwork) => (
                <ArtworkCard key={artwork._id} artwork={artwork} />
              ))}
            </div>
            <div className="text-center">
              <Link to="/marketplace" className="btn-secondary">
                View All Artworks
              </Link>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-6">No artworks available yet.</p>
            <p className="text-gray-600 mb-8">Be the first artist to showcase your work!</p>
            <Link to="/register" className="btn-primary">
              Join as Artist
            </Link>
          </div>
        )}
      </section>

      {/* Categories Section */}
      <section className="bg-gradient-to-br from-orange-100 via-amber-100 to-red-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4 african-accent inline-block">
              Explore Categories
            </h2>
            <p className="text-lg text-gray-600">
              Find the perfect art style for your collection
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Paintings', icon: '🎨', link: '/marketplace?category=painting' },
              { name: 'Digital Art', icon: '💻', link: '/marketplace?category=digital-art' },
              { name: 'Sculptures', icon: '🗿', link: '/marketplace?category=sculpture' },
              { name: 'Photography', icon: '📸', link: '/marketplace?category=photography' }
            ].map((category) => (
              <Link
                key={category.name}
                to={category.link}
                className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-african-lg transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="text-6xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {category.icon}
                </div>
                <h3 className="text-2xl font-display font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-200">
                  {category.name}
                </h3>
                <div className="mt-4 flex items-center text-orange-600 font-semibold">
                  <span>Explore</span>
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-2 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4 african-accent inline-block">
            How It Works
          </h2>
          <p className="text-lg text-gray-600">
            Simple steps to own your favorite artwork
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            {
              step: '01',
              title: 'Browse & Discover',
              description: 'Explore our curated collection of authentic African artworks from talented artists.',
              icon: '🔍'
            },
            {
              step: '02',
              title: 'Connect with Artists',
              description: 'Message artists directly, learn about their inspiration, and request custom pieces.',
              icon: '💬'
            },
            {
              step: '03',
              title: 'Secure Purchase',
              description: 'Complete your purchase securely and receive your artwork with guaranteed authenticity.',
              icon: '✨'
            }
          ].map((item) => (
            <div key={item.step} className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center text-white text-4xl mx-auto mb-6 transform group-hover:rotate-12 transition-transform duration-300 shadow-african">
                {item.icon}
              </div>
              <div className="text-6xl font-bold text-orange-200 mb-4">{item.step}</div>
              <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">
                {item.title}
              </h3>
              <p className="text-gray-600">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-orange-600 via-red-600 to-amber-600 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">
            Ready to Start Your Art Journey?
          </h2>
          <p className="text-xl text-orange-100 mb-10">
            Join thousands of art lovers discovering and collecting unique African artworks
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" className="px-8 py-4 bg-white text-orange-600 font-bold rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
              Sign Up Now
            </Link>
            <Link to="/marketplace" className="px-8 py-4 border-2 border-white text-white font-bold rounded-full hover:bg-white hover:text-orange-600 transition-all duration-300">
              Browse Artworks
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;